import Anchor from "@mui/icons-material/Anchor"
import { Delete, Star } from "@mui/icons-material"
import { Divider, IconButton, ListItemText, MenuItem, Popover, useMediaQuery } from "@mui/material"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { M_Button, M_Fieldset, M_FormFavorites, M_InputTextField, useFavorites } from "src/M_Components"
import { QuoteFormField } from "./QuoteFormField"

function GroupFavoriteActions({ groupName, t }) {
  const favCtx = useFavorites()
  const [anchorEl, setAnchorEl] = useState(null)
  const [name, setName] = useState('')

  if (!favCtx) return null

  const { saveGroupFavorite, getGroupFavorites, handleApplyFavorite, handleDeleteFavorite } = favCtx
  const groupFavs = getGroupFavorites(groupName)

  return (
    <div style={{ position: 'absolute', top: '10px', right: '18px', zIndex: 10 }}>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <Star sx={{ color: groupFavs.length > 0 ? 'var(--color-yellow, #f9a825)' : '#ccc', fontSize: '20px' }} />
      </IconButton>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => { setAnchorEl(null); setName('') }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <div style={{ minWidth: '250px' }}>
          <div style={{ padding: '12px', display: 'flex', gap: '8px', alignItems: 'center' }}>
            <M_InputTextField
              size="small"
              label={t('FavoriteName') || 'Name'}
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && name.trim()) {
                  saveGroupFavorite(groupName, name)
                  setName('')
                }
              }}
              autoFocus
              fullWidth
            />
            <M_Button
              normal
              hoverEffect
              label={t('Save') || 'Save'}
              backgroundColor={'var(--color-blue)'}
              labelColor="var(--color-white)"
              onClick={() => {
                if (name.trim()) {
                  saveGroupFavorite(groupName, name)
                  setName('')
                }
              }}
            />
          </div>
          {groupFavs.length > 0 && <Divider />}
          {groupFavs.map(fav => (
            <MenuItem
              key={fav.Id}
              onClick={() => { handleApplyFavorite(fav); setAnchorEl(null) }}
              sx={{
                '& .fav-delete': { opacity: 0, transition: 'opacity 0.2s' },
                '&:hover .fav-delete': { opacity: 1 }
              }}
            >
              <ListItemText>{fav.Description}</ListItemText>
              <IconButton
                className="fav-delete"
                size="small"
                onClick={(e) => { e.stopPropagation(); handleDeleteFavorite(fav.Id, e) }}
                sx={{ marginLeft: '8px' }}
              >
                <Delete fontSize="small" sx={{ color: 'var(--color-red, #d32f2f)' }} />
              </IconButton>
            </MenuItem>
          ))}
        </div>
      </Popover>
    </div>
  )
}

export const QuotePorts = ({ dataState, setDataState, requestMode = false }) => {
  const { t } = useTranslation()

  const allFields = [
    {
      display: t("OriginalPort"),
      required: false,
      code: "originalPortName",
      type: "foreignKey",
      entity: "Location",
      FILTER: "LocationName ne null and LocationName ne ''",
      filterField: "LocationName",
      labelFields: ["LocationName"],
      FILTER: "LocationPortStatus eq true",
      fields: [
        { key: "LocationName", value: "originalPortName" },
        { key: "Id", value: "originalPortId" },
      ],
      favoritesFieldName: t("OriginalPort"),
      favoritesGroupName: "Ports",
      favoritesVarKey: "originalPortName",
      favoritesIdKey: "originalPortId",
    },
    {
      display: t("LoadingPort"),
      required: true,
      code: "loadingPortName",
      type: "foreignKey",
      entity: "Location",
      FILTER: "LocationName ne null and LocationName ne ''",
      filterField: "LocationName",
      labelFields: ["LocationName"],
      FILTER: "LocationPortStatus eq true",
      fields: [
        { key: "LocationName", value: "loadingPortName" },
        { key: "Id", value: "loadingPortId" },
      ],
      favoritesFieldName: t("LoadingPort"),
      favoritesGroupName: "Ports",
      favoritesVarKey: "loadingPortName",
      favoritesIdKey: "loadingPortId",
    },
    {
      display: t("DischargePort"),
      required: true,
      code: "dischargePortName",
      type: "foreignKey",
      entity: "Location",
      FILTER: "LocationName ne null and LocationName ne ''",
      filterField: "LocationName",
      labelFields: ["LocationName"],
      FILTER: "LocationPortStatus eq true",
      fields: [
        { key: "LocationName", value: "dischargePortName" },
        { key: "Id", value: "dischargePortId" },
      ],
      favoritesFieldName: t("DischargePort"),
      favoritesGroupName: "Ports",
      favoritesVarKey: "dischargePortName",
      favoritesIdKey: "dischargePortId",
    },
    {
      display: t("FinalPort"),
      required: false,
      code: "finalPortName",
      type: "foreignKey",
      entity: "Location",
      FILTER: "LocationName ne null and LocationName ne ''",
      filterField: "LocationName",
      labelFields: ["LocationName"],
      FILTER: "LocationPortStatus eq true",
      fields: [
        { key: "LocationName", value: "finalPortName" },
        { key: "Id", value: "finalPortId" },
      ],
      favoritesFieldName: t("FinalPort"),
      favoritesGroupName: "Ports",
      favoritesVarKey: "finalPortName",
      favoritesIdKey: "finalPortId",
    },
  ]
  const formFields = requestMode
    ? allFields.filter((field) => field.code === "loadingPortName" || field.code === "dischargePortName")
    : allFields

  const portsVarNameDict = useMemo(() => new Map([
    ['originalPortName', dataState.originalPortName || ''],
    ['originalPortId',   dataState.originalPortId   || ''],
    ['loadingPortName',  dataState.loadingPortName  || ''],
    ['loadingPortId',    dataState.loadingPortId    || ''],
    ['dischargePortName', dataState.dischargePortName || ''],
    ['dischargePortId',  dataState.dischargePortId  || ''],
    ['finalPortName',    dataState.finalPortName    || ''],
    ['finalPortId',      dataState.finalPortId      || ''],
  ]), [
    dataState.originalPortName, dataState.originalPortId,
    dataState.loadingPortName,  dataState.loadingPortId,
    dataState.dischargePortName, dataState.dischargePortId,
    dataState.finalPortName,    dataState.finalPortId,
  ])

  return (
    <M_FormFavorites
      entityName="Quotes"
      pageUrl="Quote"
      varNameDict={portsVarNameDict}
      setVarNameDict={() => {}}
      onFavoriteLoad={(loadedData) => {
        setDataState(prev => ({ ...prev, ...loadedData }))
      }}
      onAfterApply={() => {}}
    >
      <div style={{ position: 'relative' }}>
        {!dataState.isRevisionView && <GroupFavoriteActions groupName="Ports" t={t} />}
        <M_Fieldset
          labelInside
          label={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                fontSize: "25px",
                marginLeft: "10px",
              }}
            >
              <Anchor style={{ marginRight: "10px", scale: "1.3" }} />{" "}
              {t("Ports")}
            </div>
          }
          elements={
            <div
              style={{
                display: "grid",
                justifyContent: "space-evenly",
              }}
            >
              {formFields.map((field, index) => (
                <QuoteFormField
                  key={`QuotePorts-${index}`}
                  fields={field}
                  index={index}
                  data={dataState}
                  setData={setDataState}
                  toggleable={dataState.isRevisionView ? false : dataState.isMultiEdit}
                  disabled={dataState.isRevisionView}
                />
              ))}
            </div>
          }
        />
      </div>
    </M_FormFavorites>
  )
}
